/**
 * Question Bank page — browse, filter, and search behavioral questions.
 *
 * Features:
 * - Filter by role, difficulty, competency via dropdowns
 * - Full-text search across question text
 * - Each card shows role tags, competency tags, difficulty badge
 * - "Practice" button → New Evaluation with question pre-selected
 * - "Mock Interview" button → starts a timed session
 * - "AI Generator" button → starts answer generation flow
 * - "Submit Question" — authenticated users can submit community questions
 * - Pagination with "Load More"
 */

import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import RateReviewIcon from "@mui/icons-material/RateReview";
import TimerIcon from "@mui/icons-material/Timer";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import ClearIcon from "@mui/icons-material/Clear";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PeopleIcon from "@mui/icons-material/People";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import {
  listQuestions,
  getRandomQuestion,
  submitQuestion,
  bookmarkQuestion,
  unbookmarkQuestion,
  getBookmarkedQuestionIds,
  Question,
} from "../api/client";
import { useAuth } from "../hooks/useAuth";
import { toUserSafeErrorMessage } from "../utils/errorMessage";

const ROLES = ["MLE", "PM", "TPM", "EM"];
const DIFFICULTIES = ["standard", "advanced", "senior_plus"];

// LEVEL_BANDS is a factory so its labels can be translated — call
// getLevelBands(t) inside the component, which has a translation hook.
function getLevelBands(t: TFunction) {
  return [
    { value: "entry", label: t("QuestionBank.levelBandEntry") },
    { value: "mid", label: t("QuestionBank.levelBandMid") },
    { value: "senior", label: t("QuestionBank.levelBandSenior") },
    { value: "staff", label: t("QuestionBank.levelBandStaff") },
    { value: "manager", label: t("QuestionBank.levelBandManager") },
  ];
}

const COMPETENCIES = [
  "leadership",
  "teamwork",
  "conflict_resolution",
  "technical_challenge",
  "problem_solving",
  "failure_resilience",
  "innovation",
  "communication",
  "customer_obsession",
  "ownership",
  "decision_making",
  "adaptability",
  "mentorship",
];

const difficultyColors: Record<string, "default" | "warning" | "error"> = {
  standard: "default",
  advanced: "warning",
  senior_plus: "error",
};

export default function QuestionBank() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation("pages-account");
  const LEVEL_BANDS = getLevelBands(t);

  // Filter state
  const [role, setRole] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [levelBand, setLevelBand] = useState("");
  const [competency, setCompetency] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [showBookmarked, setShowBookmarked] = useState(false);

  // Submit question dialog state
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitText, setSubmitText] = useState("");
  const [submitRoles, setSubmitRoles] = useState<string[]>([]);
  const [submitCompetencies, setSubmitCompetencies] = useState<string[]>([]);
  const [submitDifficulty, setSubmitDifficulty] = useState("standard");
  const [submitLevel, setSubmitLevel] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["questions", role, difficulty, levelBand, competency, search],
    queryFn: () =>
      listQuestions({
        role: role || undefined,
        difficulty: difficulty || undefined,
        level: levelBand || undefined,
        competency: competency || undefined,
        search: search || undefined,
        limit: 200,
      }),
  });

  // Parallel count queries for level band badges — pass current filters but
  // vary the level param so each badge shows contextual counts.
  const levelCountQueries = useQueries({
    queries: LEVEL_BANDS.map((lb) => ({
      queryKey: ["question-count", lb.value, role, difficulty, competency, search],
      queryFn: () =>
        listQuestions({
          level: lb.value,
          role: role || undefined,
          difficulty: difficulty || undefined,
          competency: competency || undefined,
          search: search || undefined,
          limit: 1,
        }),
      staleTime: 60_000, // counts don't change often
    })),
  });

  const levelCounts: Record<string, number | null> = {};
  LEVEL_BANDS.forEach((lb, i) => {
    const q = levelCountQueries[i];
    levelCounts[lb.value] = q.data ? q.data.total : null;
  });

  const hasFilters = role || difficulty || levelBand || competency || search || showBookmarked;

  // Bookmark queries (only for authenticated users)
  const { data: bookmarkedIds = [], refetch: refetchBookmarks } = useQuery({
    queryKey: ["bookmarked-question-ids"],
    queryFn: getBookmarkedQuestionIds,
    enabled: isAuthenticated,
    staleTime: 30_000,
  });

  const bookmarkedSet = useMemo(() => new Set(bookmarkedIds), [bookmarkedIds]);

  const handleToggleBookmark = useCallback(
    async (questionId: string) => {
      try {
        if (bookmarkedSet.has(questionId)) {
          await unbookmarkQuestion(questionId);
        } else {
          await bookmarkQuestion(questionId);
        }
        refetchBookmarks();
      } catch {
        // Silently fail — user can retry
      }
    },
    [bookmarkedSet, refetchBookmarks]
  );

  // Submit question mutation
  const submitMutation = useMutation({
    mutationFn: () =>
      submitQuestion({
        question_text: submitText,
        role_tags: submitRoles,
        competency_tags: submitCompetencies,
        difficulty: submitDifficulty,
        level_band: submitLevel || undefined,
      }),
    onSuccess: () => {
      setSubmitSuccess(true);
      setSubmitText("");
      setSubmitRoles([]);
      setSubmitCompetencies([]);
      setSubmitDifficulty("standard");
      setSubmitLevel("");
      setSubmitError("");
      // Refresh question list in case it got approved immediately (won't show yet, but fresh data)
      queryClient.invalidateQueries({ queryKey: ["questions"] });
    },
    onError: (err: any) => {
      setSubmitError(toUserSafeErrorMessage(err, t("QuestionBank.submitFailedFallback")));
    },
  });

  const handleSubmitQuestion = () => {
    setSubmitError("");
    if (submitText.trim().length < 20) {
      setSubmitError(t("QuestionBank.submitTooShort"));
      return;
    }
    submitMutation.mutate();
  };

  const handleCloseSubmit = () => {
    setSubmitOpen(false);
    setSubmitSuccess(false);
    setSubmitError("");
  };

  const handleClearFilters = () => {
    setRole("");
    setDifficulty("");
    setLevelBand("");
    setCompetency("");
    setSearch("");
    setSearchInput("");
    setShowBookmarked(false);
  };

  const handleSearch = () => {
    setSearch(searchInput);
  };

  const handleRandomQuestion = async () => {
    try {
      const q = await getRandomQuestion({
        role: role || undefined,
        difficulty: difficulty || undefined,
        level: levelBand || undefined,
      });
      navigate("/evaluate", {
        state: { questionId: q.id, questionText: q.question_text },
      });
    } catch {
      // If no match, just navigate to evaluate
      navigate("/evaluate");
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mb: 3 }}
        spacing={1}
      >
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
          >
            {t("QuestionBank.heading")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {isLoading
              ? t("QuestionBank.loading")
              : data
              ? t("QuestionBank.availableCount", { count: data.total })
              : t("QuestionBank.browseDefault")}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {isAuthenticated && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => {
                setSubmitSuccess(false);
                setSubmitOpen(true);
              }}
            >
              {t("QuestionBank.submitQuestionButton")}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<ShuffleIcon />}
            onClick={handleRandomQuestion}
          >
            {t("QuestionBank.randomQuestionButton")}
          </Button>
        </Stack>
      </Stack>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            {/* Row 1: Dropdowns */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
            >
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>{t("QuestionBank.roleLabel")}</InputLabel>
                <Select
                  value={role}
                  label={t("QuestionBank.roleLabel")}
                  onChange={(e) => setRole(e.target.value)}
                >
                  <MenuItem value="">{t("QuestionBank.allRolesOption")}</MenuItem>
                  {ROLES.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 140 }} size="small">
                <InputLabel>{t("QuestionBank.difficultyLabel")}</InputLabel>
                <Select
                  value={difficulty}
                  label={t("QuestionBank.difficultyLabel")}
                  onChange={(e) => setDifficulty(e.target.value)}
                >
                  <MenuItem value="">{t("QuestionBank.allLevelsOption")}</MenuItem>
                  {DIFFICULTIES.map((d) => (
                    <MenuItem key={d} value={d}>
                      {d.replace("_", " ")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 160 }} size="small">
                <InputLabel>{t("QuestionBank.levelBandLabel")}</InputLabel>
                <Select
                  value={levelBand}
                  label={t("QuestionBank.levelBandLabel")}
                  onChange={(e) => setLevelBand(e.target.value)}
                >
                  <MenuItem value="">{t("QuestionBank.allLevelsOption")}</MenuItem>
                  {LEVEL_BANDS.map((lb) => {
                    const count = levelCounts[lb.value];
                    return (
                      <MenuItem
                        key={lb.value}
                        value={lb.value}
                        disabled={count === 0}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
                          <span>{lb.label}</span>
                          {count !== null && (
                            <Chip
                              label={count}
                              size="small"
                              sx={{ height: 20, fontSize: 11, ml: "auto" }}
                              color={count === 0 ? "default" : "primary"}
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 180 }} size="small">
                <InputLabel>{t("QuestionBank.competencyLabel")}</InputLabel>
                <Select
                  value={competency}
                  label={t("QuestionBank.competencyLabel")}
                  onChange={(e) => setCompetency(e.target.value)}
                >
                  <MenuItem value="">{t("QuestionBank.allCompetenciesOption")}</MenuItem>
                  {COMPETENCIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c.replace(/_/g, " ")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Row 2: Search */}
            <Stack direction="row" spacing={1}>
              <TextField
                label={t("QuestionBank.searchLabel")}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
                size="small"
                sx={{ flexGrow: 1 }}
                placeholder={t("QuestionBank.searchPlaceholder")}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
              >
                {t("QuestionBank.searchButton")}
              </Button>
              {hasFilters && (
                <Button
                  variant="text"
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon />}
                  color="inherit"
                >
                  {t("QuestionBank.clearButton")}
                </Button>
              )}
            </Stack>

            {/* Bookmark filter */}
            {isAuthenticated && (
              <Stack direction="row" spacing={1}>
                <Chip
                  icon={<BookmarkIcon />}
                  label={
                    bookmarkedIds.length > 0
                      ? t("QuestionBank.myBookmarksLabelWithCount", { count: bookmarkedIds.length })
                      : t("QuestionBank.myBookmarksLabel")
                  }
                  variant={showBookmarked ? "filled" : "outlined"}
                  color={showBookmarked ? "primary" : "default"}
                  onClick={() => setShowBookmarked(!showBookmarked)}
                />
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Error state */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t("QuestionBank.loadErrorMessage")}
        </Alert>
      )}

      {/* Loading state */}
      {isLoading && (
        <Stack spacing={2}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardContent>
                <Skeleton variant="text" width="80%" height={24} />
                <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Empty state */}
      {!isLoading && data?.items.length === 0 && (
        <Card>
          <CardContent sx={{ textAlign: "center", py: 6 }}>
            <SearchIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {t("QuestionBank.noQuestionsFoundHeading")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("QuestionBank.noQuestionsFoundBody")}
            </Typography>
            <Button variant="outlined" onClick={handleClearFilters}>
              {t("QuestionBank.clearAllFiltersButton")}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Sparse results hint */}
      {!isLoading && data && data.total > 0 && data.total < 10 && hasFilters && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("QuestionBank.sparseResultsHint", { count: data.total })}
        </Alert>
      )}

      {/* Question list */}
      {!isLoading && data && data.items.length > 0 && (() => {
        const displayItems = showBookmarked
          ? data.items.filter((q) => bookmarkedSet.has(q.id))
          : data.items;

        if (showBookmarked && displayItems.length === 0) {
          return (
            <Card>
              <CardContent sx={{ textAlign: "center", py: 6 }}>
                <BookmarkBorderIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  {t("QuestionBank.noBookmarkedQuestionsHeading")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t("QuestionBank.noBookmarkedQuestionsBody")}
                </Typography>
                <Button variant="outlined" onClick={() => setShowBookmarked(false)}>
                  {t("QuestionBank.showAllQuestionsButton")}
                </Button>
              </CardContent>
            </Card>
          );
        }

        return (
        <Grid container spacing={2}>
          {displayItems.map((q: Question) => (
            <Grid item xs={12} key={q.id}>
              <Card
                sx={{
                  "&:hover": { boxShadow: 3 },
                  transition: "box-shadow 0.2s",
                }}
              >
                <CardContent>
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <Typography variant="body1" sx={{ mb: 1.5, flex: 1 }}>
                      {q.question_text}
                    </Typography>
                    {isAuthenticated && (
                      <Tooltip
                        title={
                          bookmarkedSet.has(q.id)
                            ? t("QuestionBank.removeBookmarkTooltip")
                            : t("QuestionBank.addBookmarkTooltip")
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleToggleBookmark(q.id)}
                          color={bookmarkedSet.has(q.id) ? "primary" : "default"}
                        >
                          {bookmarkedSet.has(q.id) ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>

                  {/* Tags row */}
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mb: 1.5 }}
                  >
                    {q.role_tags.map((tag) => (
                      <Chip
                        key={`role-${tag}`}
                        label={tag}
                        size="small"
                        color="primary"
                        variant="outlined"
                        onClick={() => setRole(tag)}
                      />
                    ))}
                    {q.competency_tags.map((tag) => (
                      <Chip
                        key={`comp-${tag}`}
                        label={tag.replace(/_/g, " ")}
                        size="small"
                        variant="outlined"
                        onClick={() => setCompetency(tag)}
                      />
                    ))}
                    <Chip
                      label={q.difficulty.replace("_", " ")}
                      size="small"
                      color={difficultyColors[q.difficulty] || "default"}
                    />
                    {q.level_band && (
                      <Chip
                        label={
                          LEVEL_BANDS.find((lb) => lb.value === q.level_band)
                            ?.label || q.level_band
                        }
                        size="small"
                        color="info"
                        variant="outlined"
                        onClick={() => setLevelBand(q.level_band!)}
                      />
                    )}
                    {q.source === "community" && (
                      <Chip
                        icon={<PeopleIcon />}
                        label={t("QuestionBank.communityChipLabel")}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    )}
                  </Stack>

                  {/* Action buttons */}
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Tooltip title={t("QuestionBank.practiceTooltip")}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<RateReviewIcon />}
                        onClick={() =>
                          navigate("/evaluate", {
                            state: { questionId: q.id, questionText: q.question_text },
                          })
                        }
                      >
                        {t("QuestionBank.practiceButton")}
                      </Button>
                    </Tooltip>
                    <Tooltip title={t("QuestionBank.mockTooltip")}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<TimerIcon />}
                        onClick={() =>
                          navigate("/mock", {
                            state: { questionId: q.id, questionText: q.question_text },
                          })
                        }
                      >
                        {t("QuestionBank.mockButton")}
                      </Button>
                    </Tooltip>
                    <Tooltip title={t("QuestionBank.aiDraftTooltip")}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={() =>
                          navigate("/generator", {
                            state: { questionId: q.id, questionText: q.question_text },
                          })
                        }
                      >
                        {t("QuestionBank.aiDraftButton")}
                      </Button>
                    </Tooltip>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        );
      })()}

      {/* Results count footer */}
      {!isLoading && data && data.items.length > 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 2, textAlign: "center" }}
        >
          {t("QuestionBank.resultsCountFooter", { shown: data.items.length, total: data.total })}
        </Typography>
      )}

      {/* Submit Question Dialog */}
      <Dialog
        open={submitOpen}
        onClose={handleCloseSubmit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PeopleIcon color="success" />
            <span>{t("QuestionBank.submitDialogTitle")}</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {submitSuccess ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              {t("QuestionBank.submitSuccessMessage")}
            </Alert>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t("QuestionBank.submitDialogIntro")}
              </Typography>

              <TextField
                label={t("QuestionBank.questionTextLabel")}
                multiline
                minRows={3}
                fullWidth
                value={submitText}
                onChange={(e) => setSubmitText(e.target.value)}
                placeholder={t("QuestionBank.questionTextPlaceholder")}
                helperText={t("QuestionBank.questionTextHelper", { length: submitText.length })}
                inputProps={{ maxLength: 500 }}
              />

              <Autocomplete
                multiple
                options={ROLES}
                value={submitRoles}
                onChange={(_, v) => setSubmitRoles(v)}
                renderInput={(params) => (
                  <TextField {...params} label={t("QuestionBank.roleTagsLabel")} size="small" />
                )}
                size="small"
              />

              <Autocomplete
                multiple
                options={COMPETENCIES}
                getOptionLabel={(o) => o.replace(/_/g, " ")}
                value={submitCompetencies}
                onChange={(_, v) => setSubmitCompetencies(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("QuestionBank.competencyTagsLabel")}
                    size="small"
                  />
                )}
                size="small"
              />

              <Stack direction="row" spacing={2}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>{t("QuestionBank.difficultyLabel")}</InputLabel>
                  <Select
                    value={submitDifficulty}
                    label={t("QuestionBank.difficultyLabel")}
                    onChange={(e) => setSubmitDifficulty(e.target.value)}
                  >
                    {DIFFICULTIES.map((d) => (
                      <MenuItem key={d} value={d}>
                        {d.replace("_", " ")}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>{t("QuestionBank.levelBandOptionalLabel")}</InputLabel>
                  <Select
                    value={submitLevel}
                    label={t("QuestionBank.levelBandOptionalLabel")}
                    onChange={(e) => setSubmitLevel(e.target.value)}
                  >
                    <MenuItem value="">{t("QuestionBank.noneOption")}</MenuItem>
                    {LEVEL_BANDS.map((lb) => (
                      <MenuItem key={lb.value} value={lb.value}>
                        {lb.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              {submitError && (
                <Alert severity="error">{submitError}</Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubmit}>
            {submitSuccess ? t("QuestionBank.closeButton") : t("QuestionBank.cancelButton")}
          </Button>
          {!submitSuccess && (
            <Button
              variant="contained"
              onClick={handleSubmitQuestion}
              disabled={submitMutation.isPending}
            >
              {submitMutation.isPending ? t("QuestionBank.submittingButton") : t("QuestionBank.submitForReviewButton")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
